import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Image, Spinner, Text } from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';

// How long the selection has to rest before the count is asked for.
const COUNT_DEBOUNCE_MS = 250;

/** A 52px row with a checkbox look; the whole row is the target. */
function OptionRow({ option, checked, onToggle, withIcon }) {
  return (
    <Box
      as="button"
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      display="grid"
      gridTemplateColumns={withIcon ? '24px 28px minmax(0, 1fr)' : '24px minmax(0, 1fr)'}
      columnGap="12px"
      alignItems="center"
      w="100%"
      minH="52px"
      px="20px"
      textAlign="left"
      _active={{ bg: 'redesign.bg.hover' }}
    >
      <Flex
        w="22px"
        h="22px"
        borderRadius="6px"
        border="2px solid"
        borderColor={checked ? 'redesign.accent.fill' : 'phone.muted'}
        bg={checked ? 'redesign.accent.fill' : 'transparent'}
        color="white"
        align="center"
        justify="center"
      >
        {checked && <Box as={LuCheck} boxSize="16px" strokeWidth={3} />}
      </Flex>
      {withIcon && (
        <Flex align="center" justify="center" fontSize="18px" lineHeight="1">
          {option.iconUrl ? (
            <Image src={option.iconUrl} alt="" boxSize="22px" objectFit="contain" borderRadius="4px" />
          ) : (option.icon || '')}
        </Flex>
      )}
      <Box minW={0}>
        <Text textStyle="phoneBody" color="redesign.heading" noOfLines={1}>{option.displayName}</Text>
        {option.displaySubName && (
          <Text textStyle="phoneMeta" color="phone.muted" noOfLines={1}>{option.displaySubName}</Text>
        )}
      </Box>
    </Box>
  );
}

/** The sheet's list: the rows, drawn from the selection the sheet holds. */
function ValueChoices({ options, selected, onToggle }) {
  const { t } = useTranslation('movements');
  const withIcon = options.some((o) => o.icon || o.iconUrl);
  return (
    <>
      <Text textStyle="phoneMeta" color="phone.muted" px="20px" pb="4px">{t('phone.filterSheet.anyOf')}</Text>
      {options.length === 0 ? (
        <Text textStyle="phoneBody" color="phone.muted" px="20px" py="24px">{t('phone.filterSheet.empty')}</Text>
      ) : options.map((option) => (
        <OptionRow
          key={option.value}
          option={option}
          checked={selected.some((v) => String(v) === String(option.value))}
          onToggle={() => onToggle(option.value)}
          withIcon={withIcon}
        />
      ))}
    </>
  );
}

/**
 * Clear and "Show N movements", in the sheet's own footer, which sits on the
 * sheet's bottom edge above the home indicator and the keyboard.
 */
function ValueFooter({ selected, count, onClear, onApply }) {
  const { t } = useTranslation('movements');
  const { close } = usePhoneSheet();

  // Applied only after the sheet's history entry is gone, so the pending back
  // step cannot revert the filter.
  const apply = async () => {
    await close();
    onApply(selected);
  };

  const showLabel = count == null
    ? t('phone.filterSheet.showAll')
    : t('phone.filterSheet.show', { count });

  return (
    <Flex gap="12px">
      <Button variant="ghost" h="52px" px="12px" flexShrink={0} onClick={onClear} isDisabled={selected.length === 0}>
        {t('phone.filterSheet.clear')}
      </Button>
      <Button
        variant="phonePrimary"
        flex="1"
        minW={0}
        px="12px"
        // "Mostrar 128 movimientos" has to fit at 320px.
        sx={{ '@media screen and (max-width: 359.98px)': { fontSize: '16px' } }}
        onClick={apply}
        rightIcon={count === null ? <Spinner size="xs" /> : undefined}
      >
        {showLabel}
      </Button>
    </Flex>
  );
}

/**
 * Pick values for one filter ("is any of" only): 52px checkbox rows, Clear,
 * and "Show N movements", counted with the selection applied before it is.
 *
 * `countFor(values)` resolves to that count; `onApply(values)` runs once the
 * sheet has closed, with an empty list meaning no filter.
 */
export default function PhoneFilterValueSheet({
  id, isOpen, onClose, title, options, selected: current, countFor, onApply,
}) {
  const [selected, setSelected] = useState(current);
  const [count, setCount] = useState(null);

  // Each opening starts from the filter as it stands, set during the render
  // that opens the sheet so no frame shows an abandoned selection.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setSelected(current);
      setCount(null);
    }
  }

  const key = JSON.stringify(selected);
  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;
    setCount(null);
    const timer = setTimeout(() => {
      countFor(selected)
        .then((n) => { if (active) setCount(n); })
        .catch(() => { if (active) setCount(undefined); });
    }, COUNT_DEBOUNCE_MS);
    return () => { active = false; clearTimeout(timer); };
  // `key` stands for `selected`, whose identity changes on every toggle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, key, countFor]);

  const toggle = (value) => setSelected((prev) => (
    prev.some((v) => String(v) === String(value))
      ? prev.filter((v) => String(v) !== String(value))
      : [...prev, value]
  ));

  return (
    <PhoneSheet
      id={id}
      isOpen={isOpen}
      onClose={onClose}
      size="detent"
      title={title}
      footer={(
        <ValueFooter
          selected={selected}
          count={count}
          onClear={() => setSelected([])}
          onApply={onApply}
        />
      )}
    >
      <ValueChoices options={options} selected={selected} onToggle={toggle} />
    </PhoneSheet>
  );
}
